import React, { useState } from "react";
import { LuArrowRight, LuArrowUpRight, LuCheck, LuDroplet, LuInfo, LuLink, LuScanLine, LuX, LuZap } from "react-icons/lu";
import { useFloating, autoUpdate, offset, flip, shift, useDismiss, useHover, useRole, useInteractions, FloatingPortal } from "@floating-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { EXAM_TYPE, EXAM_TYPE_LABEL, isActRemovedByAction } from "../../utils";

const URINE_PRECISIONS = [EXAM_TYPE.ECHANTILLON, EXAM_TYPE.H24];

const describeMatch = ({ linkingDistance, linkingTransformed } = {}) => {
  if (linkingDistance === undefined || linkingDistance === null) return null;
  if (linkingDistance === 0) return linkingTransformed ? "correspondance approximative" : "correspondance exacte";
  const typos = `${linkingDistance} faute${linkingDistance > 1 ? "s" : ""} tolérée${linkingDistance > 1 ? "s" : ""}`;
  return linkingTransformed ? `${typos}, mots en trop ignorés` : typos;
};

const explainCategory = (act, hasHeader) => {
  const context = act.context || EXAM_TYPE.DEFAULT;
  if (context === act.codeType)
    return context !== EXAM_TYPE.DEFAULT || hasHeader ? "Le contexte de l'ordonnance l'indique." : "Aucun contexte sur l'ordonnance, catégorie par défaut.";
  if (context === EXAM_TYPE.DEFAULT) return "Le contexte n'impose rien, la table en décide.";
  if (context === EXAM_TYPE.URINE && URINE_PRECISIONS.includes(act.codeType)) return "Le contexte de l'ordonnance indique des urines.";
  return `Le contexte indique ${EXAM_TYPE_LABEL[context] || context}, mais la table n'a pas cette variante.`;
};

const describeChange = (removed, added) => (removed.length && added.length ? "remplacé" : added.length ? "ajouté" : "retiré");

const ocrPercent = ({ ocrMeanConfidence } = {}) => {
  if (ocrMeanConfidence === undefined || ocrMeanConfidence === null) return null;
  return Math.round(ocrMeanConfidence <= 1 ? ocrMeanConfidence * 100 : ocrMeanConfidence);
};

const isActAddedByAction = (act, prescription) => prescription.type !== "RENEWAL" && act.codes.length > 0 && act.codesPredicted.length === 0 && !act.isAdded;

const describeStatus = (act, prescription) => {
  if (isActAddedByAction(act, prescription)) return { label: "Ajouté par une action", className: "border-yellow-200 bg-yellow-50 text-yellow-800" };
  if (act.codes.length > 0 && act.codesPredicted.length === 0) return { label: "Code ajouté", className: "border-yellow-200 bg-yellow-50 text-yellow-800" };
  if (act.isDeleted && act.codesPredicted.length > 0) return { label: "Code supprimé", className: "border-red-200 bg-red-50 text-red-700" };
  if (isActRemovedByAction(act))
    return { label: "Retiré par une action", className: "border-red-200 bg-[repeating-linear-gradient(45deg,#ef444426_0_2px,transparent_2px_8px)] text-red-700" };
  if (act.codesPredicted.length === 0) return { label: "Code non trouvé", className: "border-orange-200 bg-orange-50 text-orange-700" };
  if (act.notInserted) return { label: "Refusé par le SIL", className: "border-primary-200 bg-primary-50 text-primary-700" };
  if (act.ALD) return { label: "Inséré en ALD", className: "border-purple-200 bg-purple-50 text-purple-700" };
  return { label: "Inséré", className: "border-green-200 bg-green-50 text-green-700" };
};

const ActExplanationPopover = ({ act, prescription, referenceElement, canOpenConfiguration, hiddenCodes, canHideCodes, onReportError, onClose }) => {
  const queryClient = useQueryClient();
  const { refs, floatingStyles, context } = useFloating({
    open: true,
    onOpenChange: (next) => {
      if (next === false) onClose();
    },
    elements: { reference: referenceElement },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const [isRankingHelpOpen, setIsRankingHelpOpen] = useState(false);
  const rankingHelp = useFloating({
    open: isRankingHelpOpen,
    onOpenChange: setIsRankingHelpOpen,
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const rankingHelpInteractions = useInteractions([useHover(rankingHelp.context, { delay: { open: 150, close: 0 } }), useRole(rankingHelp.context, { role: "tooltip" })]);

  const {
    data: exam,
    isLoading: isExamLoading,
    isError: isExamError,
  } = useQuery({
    queryKey: ["exam", act.examId],
    queryFn: async () => {
      const response = await API.get(`/exam/${act.examId}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: Boolean(act.examId),
    staleTime: 60_000,
    retry: false,
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ["exam-candidates", { prescriptionId: prescription._id, actId: act._id }],
    queryFn: async () => {
      const response = await API.get(`/prescription/${prescription._id}/act/${act._id}/candidates`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: Boolean(act._id),
    staleTime: 60_000,
    retry: false,
  });

  const { data: actionEvents = [] } = useQuery({
    queryKey: ["audit-events", { documentId: prescription._id, action: "ACTIONS_APPLIED" }],
    queryFn: async () => {
      const response = await API.get(`/audit-log?documentId=${prescription._id}&collectionName=prescriptions&action=ACTIONS_APPLIED`);
      if (!response.ok) throw new Error(response.message);
      return response.data.events;
    },
    enabled: canOpenConfiguration,
    staleTime: 60_000,
  });

  const { data: actions = [] } = useQuery({
    queryKey: ["actions", { includeDeleted: true }],
    queryFn: async () => {
      const response = await API.get("/action?includeDeleted=true");
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
    enabled: canOpenConfiguration,
    staleTime: 60_000,
  });

  const hideMutation = useMutation({
    mutationFn: (code) => API.post("/hidden-code", { code }),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["hidden-codes"] });
      toast.success(`Code ${data.code} masqué : il ne compte plus comme correction`);
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de masquer ce code");
    },
  });

  const unhideMutation = useMutation({
    mutationFn: (hiddenCode) => API.delete(`/hidden-code/${hiddenCode._id}`),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ["hidden-codes"] });
      toast.success(`Code ${data.code} démasqué`);
    },
    onError: (error) => {
      capture(error);
      toast.error(error.message || "Impossible de démasquer ce code");
    },
  });

  const codes = act.isDeleted ? act.codesPredicted : act.codes;
  const isHidden = codes.length > 0 && codes.every((code) => hiddenCodes.has(code));
  const correctedCodes = act.isDeleted
    ? act.codesPredicted
    : [...act.codes.filter((code) => !act.codesPredicted.includes(code)), ...act.codesPredicted.filter((code) => !act.codes.includes(code))];
  const status = isHidden ? { label: "Code masqué", className: "border-gray-200 bg-gray-50 text-gray-500" } : describeStatus(act, prescription);
  const header = act.examTypeOffset !== undefined ? prescription.ner?.find((entity) => entity.category === "ExamType" && entity.offset === act.examTypeOffset) : null;
  const readQualifiers = [
    act.isHandwritten && "manuscrit",
    act.isCrossedOut && "barré",
    ocrPercent(act.qualitySignals) !== null && `OCR ${ocrPercent(act.qualitySignals)} %`,
  ].filter(Boolean);
  const matchDescription = describeMatch(act.qualitySignals);
  const tableCodes = exam?.table?.codes || [];
  const categoryLabel = EXAM_TYPE_LABEL[act.codeType] || act.codeType;
  const categoryCodes = tableCodes.filter((code) => code.type === act.codeType).map((code) => code.value);
  const startCodes = categoryCodes.length > 0 ? categoryCodes : act.codesPredicted;
  const removedByActions = startCodes.filter((code) => !act.codesPredicted.includes(code));
  const addedByActions = act.codesPredicted.filter((code) => !startCodes.includes(code));
  const isTranslationErrorFlagged = prescription.flags?.includes("TRADUCTION_ERROR");
  const retainedRank = candidates.findIndex((candidate) => candidate.examId === act.examId);
  const isRetainedTodaysWinner = retainedRank === 0;
  const showCandidates = candidates.length > 1 || (candidates.length === 1 && !isRetainedTodaysWinner);

  const relevantCodes = [...new Set([...startCodes, ...act.codesPredicted, ...(isActAddedByAction(act, prescription) ? act.codes : [])])];
  const actionChanges = [];
  for (const event of [...actionEvents].reverse()) {
    for (const change of event.changes) {
      const removed = (change.from || []).filter((code) => relevantCodes.includes(code));
      const added = (change.to || []).filter((code) => relevantCodes.includes(code));
      if (!removed.length && !added.length) continue;
      const key = [change.subject, ...removed, "→", ...added].join("|");
      if (actionChanges.some((previous) => previous.key === key)) continue;
      actionChanges.push({ key, name: change.subject, removed, added, actionId: actions.find((action) => action.name === change.subject)?._id });
    }
  }
  if (actionChanges.length === 1 && (removedByActions.length || addedByActions.length)) {
    actionChanges[0] = { ...actionChanges[0], removed: removedByActions, added: addedByActions };
  }
  if (actionChanges.length === 0 && (removedByActions.length || addedByActions.length)) {
    actionChanges.push({ key: "unknown", name: null, removed: removedByActions, added: addedByActions });
  }

  const extractedAt = new Date(prescription.createdAt);

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, zIndex: 60 }}
        className="w-[600px] rounded-xl bg-gray-50/95 p-1.5 shadow-[0_16px_48px_-12px_rgba(0,0,0,0.25)] ring-1 ring-gray-950/[0.08] backdrop-blur-xl"
        {...getFloatingProps()}
      >
        <div className="flex items-center gap-2 px-2.5 pb-2 pt-1.5">
          <span className="min-w-0 truncate text-sm font-semibold text-gray-900">{act.text || "N/A"}</span>
          <LuArrowRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
          <span className="shrink-0 font-mono text-sm font-semibold text-gray-900">{codes.join(", ") || "—"}</span>
          <span className={`ml-1 inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium ${status.className}`}>{status.label}</span>
          <button
            type="button"
            className="ml-auto shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
            onClick={onClose}
            title="Fermer"
          >
            <LuX className="h-4 w-4" />
          </button>
        </div>

        <div className="relative rounded-lg bg-white px-3 py-1 shadow-[0_1px_2px_rgba(0,0,0,0.05)] ring-1 ring-gray-950/[0.06]">
          <span className="pointer-events-none absolute bottom-[26px] left-[23px] top-[26px] w-px bg-gray-200" />

          <div className="relative flex items-start gap-3 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 ring-1 ring-gray-950/[0.08]">
              <LuScanLine className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs leading-4 text-gray-500">{"Lu sur l'ordonnance"}</p>
              <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-1.5 text-sm leading-5 text-gray-900">
                <span className="font-medium">« {act.text || "N/A"} »</span>
                {readQualifiers.map((qualifier) => (
                  <span key={qualifier} className="rounded bg-gray-100 px-1.5 text-xs leading-5 text-gray-600">
                    {qualifier}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="relative flex items-start gap-3 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 ring-1 ring-gray-950/[0.08]">
              <LuLink className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1 text-xs leading-4 text-gray-500">
                Synonyme reconnu dans la table
                {showCandidates && (
                  <>
                    <span className="text-gray-400">· table d'aujourd'hui</span>
                    <button
                      type="button"
                      ref={rankingHelp.refs.setReference}
                      {...rankingHelpInteractions.getReferenceProps()}
                      className="inline-flex h-4 w-4 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                      aria-label="Comment le classement est calculé"
                    >
                      <LuInfo className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </p>
              {isRankingHelpOpen && (
                <FloatingPortal>
                  <div
                    ref={rankingHelp.refs.setFloating}
                    style={{ ...rankingHelp.floatingStyles, zIndex: 70 }}
                    {...rankingHelpInteractions.getFloatingProps()}
                    className="max-w-[280px] rounded-md bg-gray-900 px-2.5 py-1.5 text-xs leading-4 text-white shadow-lg"
                  >
                    {"Classement recalculé avec la table d'aujourd'hui : le moins de fautes d'abord, puis le synonyme qui n'ignore aucun mot."}
                  </div>
                </FloatingPortal>
              )}
              <div className="mt-1.5 flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-2 ring-1 ring-gray-950/[0.04]">
                {showCandidates && retainedRank !== -1 && (
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ${
                      isRetainedTodaysWinner ? "bg-gray-900 text-white" : "bg-white text-gray-600 ring-1 ring-gray-950/[0.12]"
                    }`}
                  >
                    {retainedRank + 1}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="min-w-0 truncate text-sm font-medium leading-5 text-gray-900">
                      {!act.examId ? (
                        <span className="font-normal text-gray-500">Aucune correspondance</span>
                      ) : isExamLoading ? (
                        <span className="font-normal text-gray-500">Recherche…</span>
                      ) : isExamError || !exam ? (
                        <span className="font-normal text-gray-500">{"Synonyme supprimé de la table depuis l'extraction"}</span>
                      ) : (
                        <>« {exam.value} »</>
                      )}
                    </span>
                    {exam && matchDescription && (
                      <span className="shrink-0 rounded bg-white px-1.5 text-xs leading-5 text-gray-600 ring-1 ring-gray-950/[0.06]">{matchDescription}</span>
                    )}
                    {canOpenConfiguration && exam?.table && (
                      <a
                        href={`/table/${exam.table._id}/edit`}
                        target="_blank"
                        rel="noreferrer"
                        className="ml-auto inline-flex shrink-0 items-center gap-1 rounded text-xs text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                      >
                        Modifier <LuArrowUpRight className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {tableCodes.length > 0 && (
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="text-xs text-gray-500">Codes de la table</span>
                      {tableCodes.map((code) => {
                        const isRetainedType = !act.codeType || code.type === act.codeType;
                        return (
                          <span
                            key={code.value}
                            className={`inline-flex items-center gap-1 rounded bg-white px-1.5 text-xs leading-5 ring-1 ring-gray-950/[0.06] ${isRetainedType ? "text-gray-900" : "text-gray-400"}`}
                          >
                            <span className="font-mono font-medium">{code.value}</span>
                            <span className={isRetainedType ? "text-gray-500" : "text-gray-400"}>{EXAM_TYPE_LABEL[code.type] || code.type}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              {showCandidates && (
                <>
                  <div className="mt-1 space-y-1">
                    {candidates.map((candidate, index) => {
                      if (candidate.examId === act.examId) return null;
                      const wouldWinToday = index === 0;
                      const candidateCodes = candidate.codes.map((code) => code.value).join(", ");
                      return (
                        <div key={candidate.examId} className="flex items-start gap-3 px-3 py-1">
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums ring-1 ${
                              wouldWinToday ? "bg-amber-50 text-amber-700 ring-amber-700/20" : "bg-white text-gray-400 ring-gray-950/[0.08]"
                            }`}
                          >
                            {index + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-sm leading-5">
                              <span className="min-w-0 truncate text-gray-700" title={candidate.value}>
                                « {candidate.value} »
                              </span>
                              <span className="shrink-0 font-mono text-xs text-gray-500">{candidateCodes}</span>
                              {canOpenConfiguration && (
                                <a
                                  href={`/table/${candidate.tableId}/edit`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ml-auto inline-flex shrink-0 items-center gap-1 rounded text-xs text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                                >
                                  Ouvrir <LuArrowUpRight className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                              <span className="rounded bg-gray-100 px-1.5 text-xs leading-5 text-gray-600">
                                {describeMatch({ linkingDistance: candidate.distance, linkingTransformed: candidate.transformed })}
                              </span>
                              {wouldWinToday && (
                                <span className="rounded bg-amber-50 px-1.5 text-xs leading-5 text-amber-700 ring-1 ring-inset ring-amber-700/20">
                                  {"serait retenu aujourd'hui"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!isRetainedTodaysWinner && <p className="mt-1 text-sm leading-5 text-gray-500">{"La table a changé depuis l'extraction."}</p>}
                </>
              )}
            </div>
          </div>

          {act.codesPredicted.length > 0 && act.codeType && (
            <div className="relative flex items-start gap-3 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 ring-1 ring-gray-950/[0.08]">
                <LuDroplet className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-4 text-gray-500">Catégorie retenue</p>
                <p className="mt-0.5 text-sm font-medium leading-5 text-gray-900">
                  {categoryLabel}
                  {startCodes.length > 0 && (
                    <>
                      <span className="font-normal text-gray-500"> · donc </span>
                      <span className="font-mono">{startCodes.join(", ")}</span>
                    </>
                  )}
                </p>
                <p className="text-sm leading-5 text-gray-500">{explainCategory(act, Boolean(header))}</p>
              </div>
            </div>
          )}

          {actionChanges.map((change) => (
            <div key={change.key} className="relative flex items-start gap-3 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 ring-1 ring-gray-950/[0.08]">
                <LuZap className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-4 text-gray-500">{change.name ? `Puis l'action « ${change.name} »` : "Puis une action du laboratoire"}</p>
                <p className="mt-0.5 text-sm font-medium leading-5 text-gray-900">
                  {change.removed.length > 0 && <span className="font-mono text-gray-400 line-through">{change.removed.join(", ")}</span>}
                  {change.removed.length > 0 && change.added.length > 0 && <span className="text-gray-400"> → </span>}
                  {change.added.length > 0 && <span className="font-mono">{change.added.join(", ")}</span>}
                  <span className="font-normal text-gray-500"> · {describeChange(change.removed, change.added)}</span>
                </p>
              </div>
              {canOpenConfiguration && (
                <a
                  href={change.actionId ? `/action/${change.actionId}/edit` : "/action"}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex shrink-0 items-center gap-1 self-center rounded text-xs text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                >
                  {change.actionId ? "Ouvrir" : "Voir les actions"} <LuArrowUpRight className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}

          {codes.length > 0 && (
            <div className="relative flex items-start gap-3 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white text-gray-500 ring-1 ring-gray-950/[0.08]">
                <LuCheck className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs leading-4 text-gray-500">Code retenu</p>
                <p className="mt-0.5 font-mono text-sm font-semibold leading-5 text-gray-900">{codes.join(", ")}</p>
                {isHidden && <p className="text-sm leading-5 text-gray-500">Code masqué : ne compte pas comme correction dans les statistiques</p>}
              </div>
              {canHideCodes && correctedCodes.length > 0 && (
                <div className="flex shrink-0 flex-col items-end gap-1 self-center">
                  {correctedCodes.map((code) => {
                    const hiddenCode = hiddenCodes.get(code);
                    return (
                      <button
                        key={code}
                        type="button"
                        className="rounded text-xs text-gray-500 transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30 disabled:opacity-50"
                        disabled={hideMutation.isPending || unhideMutation.isPending}
                        onClick={() => (hiddenCode ? unhideMutation.mutate(hiddenCode) : hideMutation.mutate(code))}
                      >
                        {hiddenCode ? `Ne plus masquer ${code}` : `Masquer ${code}`}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-2.5 pb-1 pt-2 text-xs text-gray-500">
          <span>
            Extraction du {extractedAt.toLocaleDateString("fr-FR")} à {extractedAt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </span>
          {isTranslationErrorFlagged ? (
            <span>Erreur de traduction signalée</span>
          ) : (
            <button
              type="button"
              className="rounded transition-colors hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
              onClick={onReportError}
            >
              Signaler une erreur de traduction
            </button>
          )}
        </div>
      </div>
    </FloatingPortal>
  );
};

export default ActExplanationPopover;
